/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnDestroy, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationJob } from './migration.models';

/**
 * Every migration definition — drafts through finished runs. Execution
 * controls (start/pause/resume/cancel) answer to `api.available()`: without
 * the engine they are visible but disabled, never pretend-running.
 */
@Component({
  selector: 'app-mig-jobs',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressBarModule, MatTooltipModule, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-jobs.component.html'
})
export class MigrationJobsComponent implements OnInit, OnDestroy {
  private readonly service = inject(MigrationService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);
  readonly registry = inject(ConnectorRegistry);
  readonly api = inject(MigrationApiService);

  readonly loading = signal(true);
  readonly jobs = signal<MigrationJob[]>([]);
  readonly busyId = signal('');

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  load(): void {
    this.loading.set(true);
    this.service.jobs().subscribe((list) => {
      this.jobs.set(list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
      this.loading.set(false);
      this.updatePolling();
    });
  }

  /**
   * While a run is live the engine writes status/statistics back into the job
   * file every few items — re-read it so the progress bar moves. Stops itself
   * the moment nothing is running any more.
   */
  private updatePolling(): void {
    const live = this.jobs().some((j) => j.status === 'running' || j.status === 'paused');
    if (live && this.pollTimer === null) {
      this.pollTimer = setInterval(() => {
        this.service.jobs().subscribe((list) => {
          this.jobs.set(list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
          this.updatePolling();
        });
      }, 5000);
    } else if (!live) {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  connectorName(id: string): string {
    const c = this.registry.get(id);
    return c ? this.translate.instant(c.titleKey) : (id ?? '—');
  }

  statusKey(status: string): string {
    return `DMS.MIGRATION.ST_${(status || 'draft').toUpperCase().replace(/-/g, '_')}`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'aca-mig__chip--running';
      case 'completed':
        return 'aca-mig__chip--ok';
      case 'completed-warnings':
        return 'aca-mig__chip--warn';
      case 'failed':
      case 'cancelled':
      case 'interrupted':
        return 'aca-mig__chip--fail';
      default:
        return '';
    }
  }

  percent(job: MigrationJob): number {
    const s = job.statistics;
    if (!s?.documents) {
      return 0;
    }
    const done = (s.success ?? 0) + (s.failed ?? 0) + (s.skipped ?? 0);
    return Math.min(100, Math.round((done / s.documents) * 100));
  }

  docsOf(job: MigrationJob): string {
    const s = job.statistics;
    if (s?.documents === undefined) {
      return '—';
    }
    const done = (s.success ?? 0) + (s.failed ?? 0) + (s.skipped ?? 0);
    return `${done.toLocaleString()} / ${s.documents.toLocaleString()}`;
  }

  dateLabel(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  /** Reopens a draft in the wizard. */
  edit(job: MigrationJob): void {
    this.service.pendingDraft.set(job);
    this.router.navigate(['/settings/data-migration', 'new']);
  }

  control(job: MigrationJob, op: 'start' | 'pause' | 'resume' | 'cancel'): void {
    if (!job.id || this.api.available() !== true) {
      return;
    }
    if (op === 'start' || op === 'cancel') {
      const titleKey = op === 'start' ? 'DMS.MIGRATION.START_CONFIRM_TITLE' : 'DMS.MIGRATION.CANCEL_CONFIRM_TITLE';
      const msgKey = op === 'start' ? 'DMS.MIGRATION.START_CONFIRM_MSG' : 'DMS.MIGRATION.CANCEL_CONFIRM_MSG';
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: titleKey,
            message: this.translate.instant(msgKey, {
              name: job.name,
              source: this.connectorName(job.sourceConnector),
              docs: '—',
              policy: job.duplicatePolicy
            }),
            yesLabel: op === 'start' ? 'DMS.MIGRATION.START' : 'DMS.MIGRATION.CANCEL_JOB',
            noLabel: 'DMS.MIGRATION.CANCEL'
          },
          minWidth: '320px'
        })
        .afterClosed()
        .subscribe((ok) => {
          if (ok === true) {
            this.runControl(job, op);
          }
        });
      return;
    }
    this.runControl(job, op);
  }

  private runControl(job: MigrationJob, op: 'start' | 'pause' | 'resume' | 'cancel'): void {
    const id = job.id as string;
    this.busyId.set(id);
    const call =
      op === 'start' ? this.api.start(id) : op === 'pause' ? this.api.pause(id) : op === 'resume' ? this.api.resume(id) : this.api.cancel(id);
    call.subscribe({
      next: () => {
        this.busyId.set('');
        this.service.audit(`migration.${op}`, { jobId: id, detail: job.name }).subscribe({ error: () => undefined });
        this.notification.showInfo(this.translate.instant(`DMS.MIGRATION.CTL_${op.toUpperCase()}_OK`));
        this.load();
        // the engine's status write-back lands just after the call — refresh
        // once more so 'running' is seen and live polling can start
        setTimeout(() => this.load(), 2500);
      },
      error: (e) => {
        this.busyId.set('');
        this.notification.showError(e?.error?.detail ?? this.translate.instant('DMS.MIGRATION.CTL_FAIL'));
      }
    });
  }

  remove(job: MigrationJob): void {
    if (!job.id || !confirm(this.translate.instant('DMS.MIGRATION.JOB_DELETE_CONFIRM', { name: job.name }))) {
      return;
    }
    this.busyId.set(job.id);
    this.service.removeJob(job.id).subscribe({
      next: () => {
        this.busyId.set('');
        this.load();
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  canRun(job: MigrationJob): string | null {
    if (['draft', 'ready', 'paused', 'failed', 'interrupted'].includes(job.status)) {
      return job.status === 'paused' ? 'resume' : 'start';
    }
    return null;
  }

  trackById(_i: number, job: MigrationJob): string {
    return job.id ?? job.name;
  }
}
